// src/ui/ClearChatSheet.jsx — clear media, or clear everything, with the sizes
// measured rather than guessed.
//
// Sizes come from a real HEAD request per media URL (Content-Length), with
// documents read straight off the message. Anything the CDN won't answer for is
// counted as "unknown" instead of being invented — the app measures actual disk
// bytes and it would be worse to show a confident wrong number than an honest
// gap.

import React, { useEffect, useRef, useState } from 'react';
import { X, Trash, Image as ImageIcon, Video, Mic, Doc, Alert } from './icons';
import {
  scanChat, clearEverything, clearMediaOnly, MEDIA_KINDS,
} from '../services/chatClear';
import { humanBytes } from '../services/exportHistory';

const ICON = { photos: ImageIcon, videos: Video, voice: Mic, docs: Doc };

export default function ClearChatSheet({ chat, chatTitle, myUid, msgs, onClose, onCleared }) {
  const [scan, setScan] = useState(null);
  const [prog, setProg] = useState(null);
  const [ask, setAsk] = useState(null);     // 'media' | 'all'
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const abort = useRef(null);

  useEffect(() => {
    const ctl = new AbortController();
    abort.current = ctl;
    setScan(null); setProg(null);
    scanChat(msgs, chat, myUid, {
      signal: ctl.signal,
      onProgress: (d, t) => setProg(t > 4 ? { d, t } : null),
    }).then((r) => { if (!ctl.signal.aborted) { setScan(r); setProg(null); } })
      .catch(() => setScan({ counts: {}, bytes: {}, unknown: 0, textBytes: 0, total: 0 }));
    return () => ctl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.id]);

  const mediaBytes = scan ? Object.values(scan.bytes).reduce((a, b) => a + b, 0) : 0;
  const mediaCount = scan ? Object.values(scan.counts).reduce((a, b) => a + b, 0) : 0;
  const allBytes = mediaBytes + (scan?.textBytes || 0);

  const sizeText = (b, unknownSome) => {
    if (!scan) return 'Measuring…';
    if (!b && unknownSome) return 'Size unavailable';
    return humanBytes(b) + (unknownSome ? '+' : '');
  };

  const run = async (what) => {
    setBusy(true); setErr('');
    try {
      if (what === 'all') await clearEverything(chat.id, myUid, msgs);
      else await clearMediaOnly(chat.id, myUid, msgs);
      onCleared?.(what);
      onClose();
    } catch { setErr('That didn’t go through — try again.'); setBusy(false); }
  };

  return (
    <div className="ng-modal" onClick={onClose}>
      <div className="ng-card cc-card" onClick={(e) => e.stopPropagation()}>
        <div className="ng-head">
          <b><Trash width={16} height={16} /> Clear chat</b>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><X /></button>
        </div>

        <div className="ng-body">
          {prog && (
            <div className="cc-prog">
              Measuring media… {prog.d}/{prog.t}
              <span><i style={{ width: `${Math.round((prog.d / prog.t) * 100)}%` }} /></span>
            </div>
          )}

          {/* per-kind breakdown */}
          <div className="cc-kinds">
            {MEDIA_KINDS.map((k) => {
              const Icon = ICON[k.id];
              const n = scan?.counts?.[k.id] || 0;
              return (
                <div className={`cc-kind ${n ? '' : 'none'}`} key={k.id}>
                  <span className="cc-kind-ico"><Icon width={15} height={15} /></span>
                  <b>{k.name}</b>
                  <i>{n ? `${n} · ${humanBytes(scan?.bytes?.[k.id] || 0)}` : 'None'}</i>
                </div>
              );
            })}
          </div>

          {ask ? (
            <div className="ci-verify-ask">
              <span>
                {ask === 'all'
                  ? `Clear every message in ${chatTitle}? This empties the conversation on your phone too. Other people keep their copies.`
                  : `Clear the media in ${chatTitle}? The messages stay; the photos, videos, voice notes and files stop showing.`}
              </span>
              <div>
                <button className="ci-chip danger" disabled={busy} onClick={() => run(ask)}>
                  <Trash width={12} height={12} /> {busy ? 'Clearing…' : (ask === 'all' ? 'Clear everything' : 'Clear media')}
                </button>
                <button className="ci-chip" onClick={() => setAsk(null)}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <button className="cc-act" disabled={!scan || !mediaCount} onClick={() => setAsk('media')}>
                <span className="cc-act-ico"><ImageIcon width={17} height={17} /></span>
                <span>
                  <b>Clear media files only</b>
                  <i>{mediaCount ? `${mediaCount} items · ${sizeText(mediaBytes, scan?.unknown > 0)}` : 'No media in this chat'}</i>
                </span>
              </button>

              <button className="cc-act danger" disabled={!scan} onClick={() => setAsk('all')}>
                <span className="cc-act-ico"><Trash width={17} height={17} /></span>
                <span>
                  <b>Clear all messages</b>
                  <i>{scan ? `${scan.total} messages · ${sizeText(allBytes, scan.unknown > 0)}` : 'Measuring…'}</i>
                </span>
              </button>
            </>
          )}

          {scan?.unknown > 0 && (
            <div className="cc-note">
              <Alert width={14} height={14} />
              <span>{scan.unknown} item{scan.unknown === 1 ? '' : 's'} wouldn’t report a size, so the totals are a floor rather than an exact figure.</span>
            </div>
          )}
          {err && <div className="pm-err">{err}</div>}

          <div className="cc-note">
            <span>
              Both clears are keyed to your account, so they empty this conversation on
              every device you use — and nowhere else. The other person keeps their copy.
              Clearing media deletes your phone&rsquo;s downloaded files too.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
